import React, { useSyncExternalStore } from 'react';
import PhoneFrame from './PhoneFrame';
import ComposerDemo from './ComposerDemo';
import { WallScreen, AppBar, StarterCard, SCREENS, SENDER } from './PhoneScreens';
import { eyebrow, h2, barTrack, barFill, stepBadge, MAGENTA, GREY_STEP, STATUS_H, SCREEN_H, PHONE_W } from './landingTokens';

/**
 * The pinned demos (handoff §4 See it work · OPEN A PORTAL, §5 How it works for guests, §6
 * Marquee, §7 See it work · PARTY STARTER). Each is one 100vh pin inside a tall section; the
 * engine writes the frame. `L` is the page's responsive layout (pinPad, pinCols, …).
 */
const pinStyle = (L) => ({ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden', boxSizing: 'border-box', maxWidth: 1240, margin: '0 auto', padding: L.pinPad, display: 'grid', gridTemplateColumns: L.pinCols, gridTemplateRows: L.pinRows, alignItems: L.pinAlign, columnGap: 64, rowGap: 16 });
const textCol = (L) => ({ display: 'flex', flexDirection: 'column', gap: 22, minWidth: 0, order: L.textOrder });
const phoneCol = (L) => ({ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 0, order: L.phoneOrder });
const capTitle = { fontSize: 'clamp(24px,3vw,38px)', fontWeight: 800, letterSpacing: '.03em', lineHeight: 1.1 };
const capBody = { maxWidth: 440, fontSize: 15, lineHeight: 1.65, color: GREY_STEP, textWrap: 'pretty' };

const DEMO_STEPS = [
  ['Pick a photo.', 'Use a photo or clip from your camera roll, or go text only on a moving background.'],
  ['Type on it.', 'Say what you’re up to right on the Portal. Pick the font, size and spot.'],
  ['Add the venue.', 'Search a place nearby so people know where to find you.'],
  ['Set the window.', 'Without a window it goes live now and disappears after 4 hours.'],
  ['Pick who sees it.', 'Friends, close friends, your clubs, or the whole city portal.'],
  ['Send it.', 'One tap and it’s out.'],
  ['It lands on the rail.', 'Your Portal shows up at the top of their feed, with a countdown.'],
];

/** The real composer, reading the demo's state from the store so only this phone re-renders per frame. */
function LiveComposer({ store }) {
  const demo = useSyncExternalStore(store.subscribe, store.get, store.get);
  return <ComposerDemo demo={demo} sender={SENDER} />;
}

export function DemoPortalSection({ L, demoStore }) {
  return (
    <section data-sx="demoA" data-testid="demo-portal" style={{ position: 'relative', height: '520vh', background: '#000', color: '#fff' }}>
      <div style={pinStyle(L)}>
        <div style={textCol(L)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}><span style={eyebrow}>SEE IT WORK</span><h2 style={h2}>OPEN A PORTAL</h2></div>
          <div style={{ display: 'flex', gap: 5 }}>{DEMO_STEPS.map((_, i) => <span key={i} style={barTrack}><i data-dbar={i} style={barFill} /></span>)}</div>
          <div style={{ display: 'grid' }}>
            {DEMO_STEPS.map(([t, b], i) => (
              <div key={t} data-cap={i} style={{ gridArea: '1 / 1', display: 'flex', flexDirection: 'column', gap: 14, opacity: i === 0 ? 1 : 0 }}>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.24em', color: MAGENTA }}>0{i + 1}</span>
                <span style={capTitle}>{t}</span>
                <span style={capBody}>{b}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={phoneCol(L)}>
          <div data-pz="g">
            <PhoneFrame bg="#F5F5F5">
              <div style={{ position: 'absolute', left: 0, right: 0, top: STATUS_H, bottom: 0, display: 'flex', flexDirection: 'column' }}><WallScreen demo /></div>
              {/* z 5: the rail's city tabs underneath carry their own z-index and would paint through. */}
              <div data-comp="1" style={{ position: 'absolute', left: 0, top: STATUS_H, width: PHONE_W, height: SCREEN_H, zIndex: 5, background: '#000', pointerEvents: 'none', overflow: 'hidden' }}>
                <LiveComposer store={demoStore} />
              </div>
              <span data-toast="1" style={{ position: 'absolute', left: '50%', top: 290, transform: 'translate(-50%,-20px)', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', borderRadius: 999, background: '#000', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.12em', whiteSpace: 'nowrap', opacity: 0, zIndex: 9 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00FFFF' }} />YOUR PORTAL IS LIVE
              </span>
            </PhoneFrame>
          </div>
        </div>
      </div>
    </section>
  );
}

const GUEST_STEPS = [
  { title: <>CREATE YOUR <span style={{ color: MAGENTA }}>PORTAL</span></>, screen: 'profile', paras: ['We have dating profiles for love and CVs for work, but there\'s a version of you that exists outside of both. Your Portal Page is that: your social persona.', 'Hosts read it, the feed learns it, and the right invites find you.'] },
  { title: 'DISCOVER GATHERINGS NEAR YOU', screen: 'wall', paras: ['Scroll your feed. Events are ranked by how well they match your profile and where your friends will be.', 'Green lit means it\'s confirmed: grab your spot with one tap.', 'Not green lit yet? Pledge to attend, and if enough people pledge, it\'s confirmed and you\'re in. If it never happens, every dollar goes back automatically.'] },
  { title: 'FIND YOUR PEOPLE', screen: 'people', paras: ['See which events your friends are going to and join them. Met someone you\'d party with again? Add them as a friend, and you\'ll both start getting the same invites.', <>No group chat required.<br />No missed opportunity to meet again.</>] },
  { title: 'OPEN A PORTAL', screen: 'portals', paras: ['Social media tells you what\'s happened. A Portal is what could happen, spontaneously and in real life.', 'Sudden urge to hit the beach? Happy hour crew wanted for this afternoon? Open a Portal to see who\'s down. Accept the request and only then are details revealed.', 'See someone\'s Portal you want to join? One tap and you\'re one step closer to a good time.'], bold: 'Bored? Don\'t doomscroll. Open a Portal.' },
];

/** A phone running one of the app screens, black ground, 797px tall under the status bar. */
export function ScreenPhone({ screen, zoomKey = null }) {
  const Screen = SCREENS[screen];
  return (
    <div data-pz={zoomKey || undefined}>
      <PhoneFrame bg="#000" fixedHeight={false}>
        <div style={{ height: SCREEN_H, overflow: 'hidden', display: 'flex', flexDirection: 'column', background: screen === 'profile' ? '#000' : '#F5F5F5' }}><Screen /></div>
      </PhoneFrame>
    </div>
  );
}

export function GuestsSection({ L }) {
  return (
    <section id="guests" data-sx="guests" data-testid="guests" style={{ position: 'relative', height: '300vh', background: '#000', color: '#fff' }}>
      <div style={pinStyle(L)}>
        <div style={textCol(L)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}><span style={eyebrow}>HOW IT WORKS FOR GUESTS</span><h2 style={h2}>JOIN THE PARTY</h2></div>
          <div style={{ display: 'flex', gap: 6 }}>{GUEST_STEPS.map((_, i) => <span key={i} style={barTrack}><i data-g-bar={i} style={barFill} /></span>)}</div>
          <div style={{ display: 'grid' }}>
            {GUEST_STEPS.map((s, i) => (
              <div key={s.screen} data-g-text={i} style={{ gridArea: '1 / 1', display: 'flex', flexDirection: 'column', gap: L.stepGap, opacity: i === 0 ? 1 : 0, transform: `translateY(${i === 0 ? 0 : 24}px)`, transition: 'opacity .45s, transform .6s cubic-bezier(.2,.7,.2,1)', pointerEvents: i === 0 ? 'auto' : 'none' }}>
                <span style={stepBadge}>0{i + 1}</span>
                <h3 style={{ margin: 0, fontSize: L.stepH3, fontWeight: 800, letterSpacing: '.04em', lineHeight: 1.1 }}>{s.title}</h3>
                {s.paras.map((p, j) => <p key={j} style={{ margin: 0, fontSize: L.stepFs, lineHeight: 1.6, color: GREY_STEP, textWrap: 'pretty' }}>{p}</p>)}
                {s.bold && <p style={{ margin: 0, fontSize: L.stepFs, lineHeight: 1.6, fontWeight: 700, textWrap: 'pretty' }}>{s.bold}</p>}
              </div>
            ))}
          </div>
        </div>
        <div style={phoneCol(L)}>
          <div data-pz="g" style={{ position: 'relative' }}>
            {GUEST_STEPS.map((s, i) => (
              <div key={s.screen} data-g-phone={i} style={{ position: i === 0 ? 'relative' : 'absolute', inset: 0, opacity: i === 0 ? 1 : 0, transition: 'opacity .5s, transform .6s cubic-bezier(.2,.7,.2,1)' }}>
                <ScreenPhone screen={s.screen} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const MARQUEE = ['FIND YOUR PARTY', 'FIND YOUR PEOPLE', 'FIND YOUR PERSON'];

export function Marquee() {
  return (
    <div aria-hidden="true" data-testid="marquee" data-stage="1" style={{ overflow: 'hidden', background: MAGENTA, color: '#000', borderTop: '2px solid #000', borderBottom: '2px solid #000' }}>
      <div className="pl-marquee" style={{ display: 'flex', width: 'max-content' }}>
        {Array.from({ length: 12 }, (_, i) => <span key={i} style={{ flexShrink: 0, padding: '18px 0 18px 28px', fontSize: 'clamp(13px,1.4vw,16px)', fontWeight: 800, letterSpacing: '.3em', whiteSpace: 'nowrap' }}>{MARQUEE[i % 3]}{' ///'}</span>)}
      </div>
    </div>
  );
}

const STARTER_CAPS = [
  ['Guests pledge.', 'Set the number you need to break even. Guests pledge, and once you hit your goal, the event’s green lit.'],
  ['It’s green lit.', 'You’re covered before you’ve spent a dollar. Doesn’t hit? Everyone gets their money back automatically.'],
];

export function DemoStarterSection({ L }) {
  return (
    <section data-sx="demoB" data-testid="demo-starter" style={{ position: 'relative', height: '250vh', background: '#000', color: '#fff' }}>
      <div style={pinStyle(L)}>
        <div style={textCol(L)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}><span style={eyebrow}>SEE IT WORK</span><h2 style={h2}>PARTY STARTER</h2></div>
          <div style={{ display: 'grid' }}>
            {STARTER_CAPS.map(([t, b], i) => (
              <div key={t} data-bcap={i} style={{ gridArea: '1 / 1', display: 'flex', flexDirection: 'column', gap: 14, opacity: i === 0 ? 1 : 0, transition: 'opacity .3s, transform .3s' }}>
                <span style={capTitle}>{t}</span>
                <span style={capBody}>{b}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={phoneCol(L)}>
          <div data-pz="g">
            <PhoneFrame bg="#F5F5F5">
              <AppBar />
              <StarterCard live />
            </PhoneFrame>
          </div>
        </div>
      </div>
    </section>
  );
}
